import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { LoaderOpacity } from '../../components/LoadingIndicator/LoadingIndicator.style';
import { ReactTable } from '../../components/Placeholder';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PUBLISHED_POST } from '@core/ui/graphql/Profile.query';
import Empty from '../../components/Empty';
import OnError from '../../components/OnError';
import { get, isEmpty, sortBy } from 'lodash';
import { ADD_POST } from '@core/ui/navigation/constant';
import Icon from 'react-icons-kit';
import { plusRound } from 'react-icons-kit/ionicons/plusRound';
import Button from 'reusecore/src/elements/Button';
import Link from 'next/link';
import Table from '../../components/Table';
import { androidDelete } from 'react-icons-kit/ionicons/androidDelete';
import { CURRENCY } from '../../Config';
import { useUser } from '../../contexts/UserContext';
import { analytics } from '../../helpers/firebaseHelper';
import { ButtonWrapper, AdsWrapper } from './style';
import { DEL_POSTS, BOOST_POST } from '@core/ui/graphql/Mutations';
import {
  RenderAction,
  RenderDate,
  renderImage,
  RenderPrice,
  RenderTitle,
} from '../../components/Renderer/ads';
import { confirmAlert } from 'react-confirm-alert';
import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'next-i18next';
import { SpinnerLoader } from '../../components/Loader/spinnerLoader';

export default function UserListingPost({
  userId,
  isLoggedIn,
  decrementAdsCount,
}) {
  let keys = [];
  const router = useRouter();
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [adsData, setAdsData] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [adsTable, setAdsTable] = useState([]);

  const { data, loading, error } = useQuery(GET_PUBLISHED_POST, {
    variables: {
      USERNAME: userId,
      LIMIT: 50,
    },
    ssr: false,
    fetchPolicy: 'cache-and-network',
    skip: !userId,
  });
  const {
    state: { boost: userBoost },
  } = useUser();

  const [deleteMutation] = useMutation(DEL_POSTS);
  const [handleBoostMutation] = useMutation(BOOST_POST, {
    // Not working trying to update apollo cache manually
    // @link https://www.apollographql.com/docs/react/caching/cache-interaction/#using-updatequery-and-updatefragment
    // update: async (cache, payload) => {
    //   const newData = payload.data.boostPost.data;
    //   newData.price = 150
    //   const res = await cache.writeQuery({
    //     query: gql`
    //       query UpdatePost($id: Int!) {
    //         post(id: $id) {
    //           id
    //           price
    //         }
    //       }
    //     `,
    //     data: {
    //       post: newData
    //     },
    //     variables: {
    //       id: newData.id
    //     }
    //   })
    // }
  });

  let { author = {} } = data || {};

  useEffect(() => {
    if (author) {
      setAdsData(sortAds(author.posts ? author.posts.data : []));
    }
  }, [data]);

  useEffect(() => {
    if (adsData instanceof Array) {
      setAdsTable(
        adsData.map(element => {
          const actions = [
            <Button
              className="boost"
              iconPosition="left"
              loaderColor="white"
              onClick={() => boostItem(element.id)}
              title={t('adsView.sidebar.boost.action')}
            />,
            <Link href={`/${ADD_POST}/${element.sid}`}>
              <a>{t('update')}</a>
            </Link>,
            <a className="delete" onClick={() => confirmDelete(element.id)}>
              {t('delete')}
            </a>,
          ];
          return {
            key: element.id,
            image: renderImage(element),
            title: <RenderTitle element={element} />,
            price: <RenderPrice element={element} />,
            createdAt: <RenderDate element={element} />,
            action: <RenderAction actions={actions} />,
          };
        })
      );
    }
  }, [adsData]);

  const sortAds = data => {
    return sortBy(data, o => (o ? -o.updatedAt._seconds : 0));
  };

  const confirmDelete = ids => {
    if (isEmpty(ids)) {
      addToast(t('profile.alerts.delete.empty'), {
        appearance: 'warning',
        autoDismiss: true,
      });
      return;
    }
    const isArray = ids instanceof Array;
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="custom-ui-delete-modal">
            <h3>{t('adsView.sidebar.delete.modal.title')}</h3>
            <p>{t('adsView.sidebar.delete.modal.msg')}</p>
            <button onClick={onClose}>{t('No')}</button>
            <button
              onClick={async () => {
                await deleteAds(isArray ? ids : [ids]);
                onClose();
              }}
            >
              {t('adsView.sidebar.delete.modal.confirm')}
            </button>
          </div>
        );
      },
    });
  };

  const deleteAds = async ids => {
    if (isEmpty(ids)) {
      addToast(t('profile.ads.delete.empty'), {
        appearance: 'warning',
        autoDismiss: true,
      });
      return;
    }
    const res = await deleteMutation({
      variables: {
        post: { ids },
      },
    });
    if (get(res, 'data.deletePosts.success', false)) {
      decrementAdsCount();
      const toastMessage =
        ids.length > 1
          ? 'adsView.sidebar.delete.res.plural'
          : 'adsView.sidebar.deleted';
      addToast(t(toastMessage), {
        appearance: 'success',
        autoDismiss: true,
      });
    } else {
      addToast(t('adsView.sidebar.delete.res.error'), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  const cta = (
    <Button
      title={t('header.publish')}
      onClick={() => router.push(`/${ADD_POST}?id=new`, `/${ADD_POST}/new`)}
      icon={<Icon icon={plusRound} size={20} color="#595959" />}
      iconPosition="left"
      bg="#00BCD4"
      color="#FFF"
      border="1px solid #00BCD4"
      aria-label={t('header.publish')}
    />
  );

  const onSelect = ids => {
    keys = ids;
  };

  const boostItem = async id => {
    if (isLoggedIn) {
      // check if user can boost the ad
      if (userBoost >= 3) {
        addToast(<p>{t('adsView.sidebar.boost.modal.suberror')}</p>, {
          appearance: 'warning',
          autoDismiss: true,
        });
        return;
      }
      setBtnLoading(true);
      const ads = adsData.map((item, i) => {
        let ads = { updatedAt: item.updatedAt, ...item };
        if (ads.id === id) {
          const now = new Date();
          ads.updatedAt = {
            _seconds: ~~(now.getTime() / 1000),
          };
        }
        return { ...ads };
      });
      setAdsData(sortAds(ads));
      const { data } = await handleBoostMutation({
        variables: {
          postId: id,
        },
      });
      setBtnLoading(false);
      const res = data.boostPost;
      if (res.success) {
        if (typeof window !== 'undefined' && analytics) {
          analytics.logEvent('boost_ad', {
            currency: CURRENCY,
            items: [
              {
                id: adsData.sid,
                name: adsData.title,
                category: adsData.category,
                price: adsData.price,
              },
            ],
            value: adsData.price,
          });
        }
      }
      addToast(
        <p>
          {t(res.message)}
          <br />
          <small>{t(res.extra)}</small>
        </p>,
        {
          appearance: res.success ? 'success' : 'warning',
          autoDismiss: true,
        }
      );
    }
  };

  const columns = [
    {
      Header: (
        <ButtonWrapper>
          <span onClick={() => deleteAds(keys)} className="btn delete">
            <Icon size={25} mr={10} icon={androidDelete} /> {t('delete')}
          </span>
        </ButtonWrapper>
      ),
      accessor: 'image',
    },
    {
      Header: ' ',
      accessor: 'title',
    },
    {
      Header: t('publishView.price'),
      accessor: 'price',
    },
    {
      Header: t('creationDate'),
      accessor: 'createdAt',
    },
    {
      Header: <span style={{ marginLeft: '11%' }}>{t('action')}</span>,
      accessor: 'action',
    },
  ];

  if (error) {
    return <OnError />;
  }

  return (
    <AdsWrapper>
      {!loading && get(data, 'author.posts.data', null) === null ? (
        <Empty
          title={t('profile.ads.empty.title')}
          description={t('profile.ads.empty.add')}
          extra={cta}
        />
      ) : loading ? (
        <>
          <ReactTable />
          <SpinnerLoader className="mobile" />
        </>
      ) : (
        <>
          {btnLoading ? <LoaderOpacity /> : null}
          <Table
            className="responsive"
            columns={columns}
            data={adsTable}
            onSelect={onSelect}
          />
        </>
      )}
    </AdsWrapper>
  );
}
